import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  APPROVAL_STATUS,
  LOG_STEP,
  PUSH_STATUS,
  RENDER_STATUS,
  StatusChip,
  absoluteTime,
  relativeTime,
} from "./constants";

/**
 * The full lifecycle of one push, in order: submitted, approved/rejected,
 * assets promoted out of private staging, sent, published/failed/retried.
 * Entries come from AdminPortalMrssPush.history — an unmapped step name still
 * renders (grey), so the backend can add steps without this knowing them.
 */
export default function LogsDialog({ row, onClose }) {
  const entries = row?.history || [];

  return (
    <Dialog open={Boolean(row)} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1.25 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
          <Box minWidth={0}>
            <Typography variant="h6" fontWeight={700} noWrap sx={{ fontSize: 17 }}>
              Activity — {row?.video_title || row?.unique_key}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {row?.admin_portal_feed_title || row?.admin_portal_feed_id}
              {row?.pushed_by ? ` · submitted by ${row.pushed_by}` : ""}
            </Typography>
            {row?.created_date && (
              <Tooltip title={absoluteTime(row.created_date)}>
                <Typography variant="caption" color="text.secondary" sx={{ cursor: "help" }}>
                  Created {relativeTime(row.created_date)}
                </Typography>
              </Tooltip>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Current standing, up top — the "where is this right now" answer,
            before the step-by-step history below it. */}
        <Box display="flex" gap={0.75} flexWrap="wrap" mb={2}>
          <StatusChip map={APPROVAL_STATUS} value={row?.approval_status} />
          <StatusChip map={PUSH_STATUS} value={row?.status} />
          {row?.video_source === "EDITED" && row?.render_status && (
            <StatusChip map={RENDER_STATUS} value={row.render_status} />
          )}
          {row?.is_staged && (
            <Chip
              label="Awaiting move to final storage"
              size="small"
              sx={{ color: "#0d9488", bgcolor: "#f0fdfa", fontWeight: 600, fontSize: 11, height: 22, border: "1px solid #0d948833" }}
            />
          )}
        </Box>

        {row?.error_message && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {row.error_message}
          </Alert>
        )}

        {entries.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No log entries recorded for this push yet.
          </Alert>
        ) : (
          <Box>
            {entries.map((entry, i) => {
              const step = LOG_STEP[entry.status] || { label: entry.status || "—", color: "#64748b" };
              const isLast = i === entries.length - 1;
              return (
                <Box key={i} sx={{ display: "flex", gap: 1.5, pb: isLast ? 0 : 2.25, position: "relative" }}>
                  {/* Timeline rail: dot per entry, connecting line except
                      after the last one. */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                    <Box
                      sx={{
                        width: 11,
                        height: 11,
                        borderRadius: "50%",
                        bgcolor: "#fff",
                        border: `2.5px solid ${step.color}`,
                        mt: 0.4,
                      }}
                    />
                    {!isLast && <Box sx={{ width: 2, flex: 1, bgcolor: "#e2e8f0", mt: 0.5 }} />}
                  </Box>
                  <Box
                    sx={{
                      minWidth: 0,
                      flex: 1,
                      p: 1.1,
                      borderRadius: 2,
                      bgcolor: "#f8fafc",
                      border: "1px solid #eef2f6",
                    }}
                  >
                    <Box display="flex" alignItems="baseline" justifyContent="space-between" gap={1}>
                      <Typography variant="body2" fontWeight={700} sx={{ color: step.color, fontSize: 13 }}>
                        {step.label}
                      </Typography>
                      {entry.created_date && (
                        <Tooltip title={absoluteTime(entry.created_date)}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ whiteSpace: "nowrap", cursor: "help", fontVariantNumeric: "tabular-nums" }}
                          >
                            {relativeTime(entry.created_date)}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                    {entry.message && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        {entry.message}
                      </Typography>
                    )}
                    {entry.error && (
                      <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.25, wordBreak: "break-word" }}>
                        {entry.error}
                      </Typography>
                    )}
                    {(entry.video_url || entry.thumb_url) && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 0.5, wordBreak: "break-all", fontFamily: "monospace", fontSize: 10, color: "#94a3b8" }}
                      >
                        {entry.video_url || entry.thumb_url}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="inherit" sx={{ textTransform: "none", borderRadius: 2, borderColor: "#e2e8f0" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
